export type Node = {
  name?: string,
  kind: string,
  // We will put known meta props here, but leave it open
  meta?: { 
    [key: string]: any,
    "intersection-naming"?: "first-only",
    "references"?: string,
    "referencedIn"?: string,
    isLegacy?: boolean,
    uuid?: "hidden"
    migration?: string,
    maxItems?: number,
    validateInput?: string,
  },
  // Put known propMeta props here, and close it up
  propMeta?: Record<string, {
    name?: string,
    comment?: string,
    width?: '1' | '2' | '3' | '4',
    display?: string,
    collapsible?: boolean | 'starts-shown' | 'starts-collapse',
    hidden?: "yes",
    placement?: "top" | "bottom",
    background?: string, // e.g. "gray-100"
    direction?: "rtl" | "ltr"
    uuid?: "hidden"
    onChangeClearNearest?: string
    // Injects a customUI renderer (by elementType) next to this property; customUIToggle gates it
    // behind a show/hide button. See @customUI / @customUIToggle annotations.
    customUI?: string
    customUIToggle?: string
    validateJS?: string
    component?: string
    validateInput?: string
    format?: string
    formatMessage?: string
    language?: string
    macros?: string
    deprecated?: string
    default?: string
    maxItems?: number
    tailwindColor?: string
    stacked?: string
    readonly?: string
    references?: string
    // Narrows an @references option list to the options valid for a sibling property's value.
    referencesFilterBy?: string
  }>,
  source?: string
} & ({
  kind: 'object'
  props: Record<string, Node>,
  required: string[],
} | {
  kind: 'string'
} | {
  kind: 'number'
} | {
  kind: 'boolean'
} | {
  kind: 'union',
  options: Node[]
} | {
  kind: 'literal',
  value: any
} | {
  kind: 'ref',
  label: string
} | {
  kind: 'typeParameter',
  label: string
} | {
  kind: 'array',
  element: Node
} | {
  kind: 'intersection',
  constraints: Node[]
});

export type Schema = Record<string, Node>;

/** Follows a chain of `ref` nodes to the type they name. Loop-guarded against a cyclic schema. */
export function deref(node: Node | undefined, schema: Schema): Node | undefined {
  let current = node;
  for (let hops = 0; current?.kind === 'ref' && hops < 100; hops++) {
    current = schema[current.label];
  }
  return current;
}

/**
 * The value-shape rules the editor enforces through per-row UI (a `@format` regex,
 * a `@uuid` identifier that must not be blank) but `checkRawMatches` does not — it
 * treats every string as an acceptable string.
 *
 * Collected from the node itself and from the containing object's propMeta, because
 * annotations land in either place depending on whether they were written on the
 * type or on the property.
 */
export function stringConstraints(
  node: Node | undefined,
  parentPropMeta?: NonNullable<Node['propMeta']>[string],
): { format?: string, formatMessage?: string, isIdentifier: boolean } {
  return {
    format: node?.meta?.format ?? parentPropMeta?.format,
    formatMessage: node?.meta?.formatMessage ?? parentPropMeta?.formatMessage,
    isIdentifier: !!(node?.meta?.uuid ?? parentPropMeta?.uuid),
  };
}

/**
 * @param strictObjects - when true, a value only matches an `object` node if it is
 * actually a plain object. Historically (and by default) a primitive passes any
 * object type that has no required props (`Object.keys(42)` is empty), and an
 * array passes any object type at all when exactMatch is off (its numeric keys
 * are simply "unknown properties"). Production documents contain legacy data
 * that relies on that leniency, and the publish-time validation and the editor
 * paste path go through here — so strictness is OPT-IN, used only where a false
 * match is worse than a lenient one (the transaction engine's union narrowing).
 */
export function checkRawMatches(node: Node, schema: Schema, obj: any, exactMatch?: boolean, strictObjects?: boolean): boolean {
  if (obj === undefined || obj === null) {
    return false;
  }

  switch(node.kind) {
    case 'object': {
      if (strictObjects && (typeof obj !== 'object' || Array.isArray(obj))) {
        return false;
      }

      // Check that all required properties are present
      if (node.required.some(p => obj[p] === undefined)) {
        return false;
      }

      // Check that all known properties are typed properly. If exactMatch is true, then
      // the object and the node must have exactly the same properties, no more, no less.
      const objKeys = Object.keys(obj);
      if (exactMatch && objKeys.length !== Object.keys(node.props).length) {
        return false;
      }
      return objKeys.every(p =>
        node.props[p]
          ? checkRawMatches(node.props[p], schema, obj[p], exactMatch, strictObjects)
          : !exactMatch
      );
    }
    case 'array': {
      return Array.isArray(obj) && (obj as any[]).every(v => checkRawMatches(node.element, schema, v, exactMatch, strictObjects));
    }
    case 'ref': {
      return checkRawMatches(schema[node.label], schema, obj, exactMatch, strictObjects);
    }
    case 'string': {
      return typeof obj === 'string';
    }
    case 'number': {
      return typeof obj === 'number';
    }
    case 'boolean': {
      return typeof obj === 'boolean';
    }
    case 'literal': {
      return obj === node.value;
    }
    case 'union': {
      return node.options.some(v => checkRawMatches(v, schema, obj, exactMatch, strictObjects));
    }
    case 'intersection': {
      return node.constraints.every(v => checkRawMatches(v, schema, obj, exactMatch, strictObjects));
    }
    default: {
      return false;
    }
  }
}
